import type { BoardObjectV2, SceneV2 } from "@bezpart-board/shared";

import type { FlatBoardOp } from "./opsTypes.js";

import type { UndoEntry } from "./SceneStore.js";



const LEGACY_STYLE_KEYS = [

  "stroke",

  "strokeWidth",

  "strokeOpacity",

  "strokeLinecap",

  "strokeLinejoin",

  "strokeDash",

  "fill",

  "color",

] as const;



export function undoEntryForAdd(object: Record<string, unknown>): UndoEntry {

  const id = String(object.id);

  return {

    forward: [{ type: "add_object", object }],

    inverse: [{ type: "delete_object", id }],

  };

}



export function undoEntryForAdds(objects: Record<string, unknown>[]): UndoEntry {

  const forward = objects.map((object) => ({ type: "add_object" as const, object }));

  const inverse = objects

    .map((object) => ({ type: "delete_object" as const, id: String(object.id) }))

    .reverse();

  return { forward, inverse };

}



export function undoEntryForDelete(object: BoardObjectV2): UndoEntry {

  const snapshot = JSON.parse(JSON.stringify(object)) as Record<string, unknown>;

  return {

    forward: [{ type: "delete_object", id: object.id }],

    inverse: [{ type: "add_object", object: snapshot }],

  };

}



export function undoEntryForDeletes(objects: BoardObjectV2[]): UndoEntry {

  const snapshots = objects.map(

    (o) => JSON.parse(JSON.stringify(o)) as Record<string, unknown>

  );

  return {

    forward: objects.map((o) => ({ type: "delete_object" as const, id: o.id })),

    inverse: snapshots.map((object) => ({ type: "add_object" as const, object })).reverse(),

  };

}



function sceneObjectsFrom(sceneOrObjects: SceneV2 | BoardObjectV2[]): BoardObjectV2[] {

  if (Array.isArray(sceneOrObjects)) return sceneOrObjects;

  return Array.isArray(sceneOrObjects.objects) ? sceneOrObjects.objects : [];

}



/** Inverse patch mirrors mergeObjectPatch (applyLocalOps) for changed keys only. */

function inversePatchForObject(

  obj: BoardObjectV2,

  patch: Record<string, unknown>

): Record<string, unknown> {

  const inv: Record<string, unknown> = {};

  const record = obj as Record<string, unknown>;



  for (const key of Object.keys(patch)) {

    if (key === "style" && patch.style && typeof patch.style === "object") {

      const stylePatch = patch.style as Record<string, unknown>;

      const prevStyle = (obj.style ?? {}) as Record<string, unknown>;

      const invStyle: Record<string, unknown> = {};

      for (const sk of Object.keys(stylePatch)) {

        invStyle[sk] = prevStyle[sk];

      }

      inv.style = invStyle;

      continue;

    }



    if (key === "bindings") {

      inv.bindings = record.bindings ?? null;

      continue;

    }



    if ((LEGACY_STYLE_KEYS as readonly string[]).includes(key)) {

      if (key === "color") {

        inv.color = obj.style?.backgroundColor ?? record.color;

      } else {

        inv[key] = obj.style?.[key as keyof typeof obj.style] ?? record[key];

      }

      continue;

    }



    inv[key] = record[key];

  }



  return inv;

}



export function undoEntryForUpdates(

  sceneOrObjects: SceneV2 | BoardObjectV2[],

  patches: { id: string; patch: Record<string, unknown> }[]

): UndoEntry {

  const objects = sceneObjectsFrom(sceneOrObjects);



  const forward: FlatBoardOp[] = patches.map((p) => ({

    type: "update_object",

    id: p.id,

    patch: p.patch,

  }));



  // Индекс вместо find на каждый патч: при переносе выделения из сотен объектов
  // это давало полный проход по сцене на каждый из них.
  const byId = new Map<string, BoardObjectV2>();
  for (const o of objects) byId.set(o.id, o);

  const inverse: FlatBoardOp[] = patches

    .map((p) => {

      const obj = byId.get(p.id);

      if (!obj) return null;

      const invPatch = inversePatchForObject(obj, p.patch);

      if (!Object.keys(invPatch).length) return null;

      return { type: "update_object" as const, id: p.id, patch: invPatch };

    })

    .filter(Boolean) as FlatBoardOp[];



  return { forward, inverse: inverse.reverse() };

}

